import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox } from '@/components/shared/ui/primitives/CheckBox';
import { formatDate, formatTime } from '@/lib/helper/formatDateTime';
import useFormDialogStore from '@/lib/zustand/form-dialog-store';
import { useDeleteTask, useEditTask } from '@/lib/api/task-api';
import { Trash } from 'lucide-react';
import { cn } from '@/lib/helper/utils';
import { ApiErrorPlaceHolder, LoadingPlaceHolder, NoDataPlaceHolder, } from '@/components/shared/ui/placeholder-ui/ListPlaceHolder';
import { toast } from 'sonner';
import { useEffect, useRef } from 'react';
import LoadMoreButton from '@/components/shared/ui/placeholder-ui/LoadMoreButton';
import TaskListLoader from '@/components/shared/ui/placeholder-ui/TaskListLoader';
import { forwardRef } from 'react';
import { ScrollArea } from '@/components/shared/ui/primitives/ScrollArea';
import ActionPageEventListPlaceholder from '@/components/shared/ui/placeholder-ui/ActionPageEventListPlaceholder';
import SearchNotFoundPlaceholder from '@/components/shared/ui/placeholder-ui/SearchNotFoundPlaceHolder';
export const TaskList = ({ addFn, setFilter, loader = 'skeleton', page, queryResult, className, }) => {
    const { data: tasksData, isLoading, isError, refetch } = queryResult;
    const lastItemRef = useRef(null);
    // scroll down to bottom after fetching more data if user clicked load more
    useEffect(() => {
        if (!tasksData || tasksData?.items?.length - 5 <= 30) {
            return;
        }
        if (lastItemRef.current) {
            lastItemRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [tasksData?.items?.length]);
    // loading logics
    if (isLoading) {
        if (loader === 'skeleton') {
            return (_jsx("div", { className: "px-2", children: _jsx(TaskListLoader, {}) }));
        }
        else {
            return _jsx(LoadingPlaceHolder, {});
        }
    }
    // handle network error
    if (isError || !tasksData) {
        return _jsx(ApiErrorPlaceHolder, { retryFn: refetch });
    }
    // handle search not found
    if (tasksData?.total === 0) {
        // handle empty tasks list
        if (page === 'projectPage') {
            return _jsx(NoDataPlaceHolder, { addFn: addFn, children: "Add a task" });
        }
        if (tasksData.unfilteredTotal === 0) {
            return _jsx(ActionPageEventListPlaceholder, { addFn: addFn, children: "Add New Task" });
        }
        return (_jsx(SearchNotFoundPlaceholder, { children: "No task matched your search." }));
    }
    const taskListItems = tasksData.items.map((taskData, index, arr) => {
        const isLast = index === arr.length - 1;
        return (_jsx(TaskListItem, { data: taskData, openedOn: page, ref: isLast ? lastItemRef : undefined }, taskData.id));
    });
    const remainingTasks = tasksData.total - tasksData.items.length > 0;
    const handleLoadMore = () => {
        const curentLength = tasksData?.items.length;
        if (!curentLength) {
            return;
        }
        setFilter((prev) => {
            return {
                ...prev,
                take: curentLength + 13,
            };
        });
    };
    return (_jsxs(ScrollArea, { className: cn('flex flex-col h-0 grow gap-1 overflow-y-auto', className), children: [taskListItems, remainingTasks && (_jsx("div", { className: "flex justify-center pt-3 pb-8", children: _jsx(LoadMoreButton, { loadMoreFn: handleLoadMore, isLoading: isLoading }) }))] }));
};
const TaskListItem = forwardRef(({ data, openedOn }, ref) => {
    const editTask = useEditTask();
    const deleteTask = useDeleteTask();
    const setFormDialogState = useFormDialogStore((state) => state.setFormDialogState);
    const formattedDate = formatDate(data.dueAt, 'LONG');
    const formattedTime = formatTime(data.dueAt ?? '');
    const handleOpenDialog = () => {
        setFormDialogState({
            isOpen: true,
            mode: 'edit',
            openedOn: openedOn,
            type: 'task',
            entity: 'task',
            data: data,
        });
    };
    const handleCheck = async (checked) => {
        setTimeout(async () => {
            try {
                await editTask.mutateAsync({
                    id: data.id,
                    status: checked ? 'completed' : 'pending',
                });
            }
            catch {
                toast.error('Something went wrong. Please try again.');
            }
        }, checked ? 400 : 0);
    };
    const handleDelete = () => {
        deleteTask.mutate(data.id);
    };
    const isPastDue = new Date(data.dueAt ?? '') < new Date();
    return (_jsxs("div", { ref: ref ? ref : undefined, className: "grid grid-cols-[24px_auto] cursor-default hover:bg-background transition-colors duration-75 py-1 pl-2 rounded-lg group relative", children: [_jsxs("div", { className: "w-[24px] flex items-start pt-1", children: [data.status === 'pending' && (_jsx(Checkbox, { onCheckedChange: (checked) => handleCheck(checked), className: "h-[16px] w-[16px] shadow-none rounded-full opacity-100 mr-2 transition-all duration-150" })), data.status === 'completed' && (_jsx(Checkbox, { onCheckedChange: (checked) => handleCheck(checked), checked: true, className: "h-[16px] w-[16px] shadow-none rounded-full opacity-100 mr-2 transition-all duration-150" }))] }), _jsx("p", { onClick: handleOpenDialog, className: cn(isPastDue && 'text-general-red'), children: data.name }), _jsx("div", {}), _jsxs("div", { className: "flex text-sm text-secondary gap-5", onClick: handleOpenDialog, children: [_jsx("p", { className: "w-fit", children: formattedDate }), formattedTime && data.isWithTime && _jsx("p", { className: "w-[60px]", children: formattedTime })] }), _jsx("div", { className: "h-full absolute flex items-center pr-3 right-0 opacity-0 group-hover:opacity-100 transition-opacity sm:hidden", onClick: handleDelete, children: _jsx(Trash, { className: "h-4 cursor-pointer text-secondary hover:text-primary" }) })] }));
});
TaskListItem.displayName = 'TaskListItem';
